<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav active="cycles" />
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ cycleId ? 'Edit Review Cycle' : 'Add Review Cycle' }}
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.name"
                label="Cycle name"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.reviewYear"
                label="Year being reviewed"
                :rules="rules.year"
                required
                @update:model-value="suggestName"
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="cycleId">
              <oxd-input-field
                v-model="form.status"
                type="select"
                label="Status"
                :options="statusOptions"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="form.periodStart"
                label="Review period start"
                :rules="rules.periodStart"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="form.periodEnd"
                label="Review period end"
                :rules="rules.periodEnd"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="form.dueDate"
                label="Submission due date"
                :rules="rules.dueDate"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-actions>
          <required-text />
          <oxd-button display-type="ghost" label="Cancel" @click="onCancel" />
          <submit-button :label="cycleId ? 'Save' : 'Save & choose staff'" />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div v-if="cycleId" class="orangehrm-card-container apcwo-pr-picker">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Eligible Staff
      </oxd-text>
      <oxd-text tag="p" class="apcwo-pr-hint">
        Choose who takes part in this review. Use the filters, then "Select all
        shown" and untick anyone to exclude. People who have already started
        their review stay in the cycle.
      </oxd-text>
      <oxd-divider />

      <div class="apcwo-pr-picker__filters">
        <oxd-input-field
          v-model="filters.text"
          placeholder="Search name or ID"
        />
        <oxd-input-field
          v-model="filters.subunit"
          type="select"
          :options="subunitOptions"
          placeholder="All teams"
        />
        <oxd-input-field
          v-model="filters.jobTitle"
          type="select"
          :options="jobTitleOptions"
          placeholder="All job titles"
        />
      </div>
      <div class="apcwo-pr-picker__bar">
        <span>
          <strong>{{ selectedCount }}</strong> selected of
          {{ participants.length }} · showing {{ filtered.length }}
        </span>
        <span class="apcwo-pr-picker__bar-actions">
          <oxd-button
            label="Select all shown"
            display-type="ghost"
            @click="setShown(true)"
          />
          <oxd-button
            label="Deselect all shown"
            display-type="ghost"
            @click="setShown(false)"
          />
        </span>
      </div>

      <div class="apcwo-pr-picker__list">
        <label
          v-for="p in filtered"
          :key="p.empNumber"
          :class="['apcwo-pr-picker__row', {'is-locked': p.locked}]"
        >
          <input v-model="p.selected" type="checkbox" :disabled="p.locked" />
          <span class="apcwo-pr-picker__name">{{ p.name }}</span>
          <span class="apcwo-pr-picker__meta">
            {{ [p.jobTitle, p.subunit].filter(Boolean).join(' · ') }}
          </span>
          <status-badge
            v-if="p.status && p.status !== 'not_started'"
            :status="p.status"
          />
        </label>
        <oxd-text v-if="!filtered.length" tag="p" class="apcwo-pr-hint">
          No employees match the filters.
        </oxd-text>
      </div>

      <oxd-form-actions>
        <submit-button
          label="Save participants"
          :disabled="isLoading"
          @click="saveParticipants"
        />
      </oxd-form-actions>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
  endDateShouldBeAfterStartDate,
} from '@ohrm/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import HrNav from '../components/HrNav.vue';
import StatusBadge from '../components/StatusBadge.vue';
import {API_BASE, errorMessage} from '../util/helpers';

export default {
  name: 'CycleSave',
  components: {
    'hr-nav': HrNav,
    'status-badge': StatusBadge,
  },
  props: {
    cycleId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const http = new APIService(window.appGlobal.baseUrl, `${API_BASE}/cycles`);
    return {http, userDateFormat};
  },
  data() {
    const year = new Date().getFullYear() - 1;
    return {
      isLoading: false,
      participants: [],
      filters: {text: '', subunit: null, jobTitle: null},
      statusOptions: [
        {id: 'open', label: 'Open'},
        {id: 'closed', label: 'Closed'},
      ],
      form: {
        name: `${year} Review & ${year + 1} Plan`,
        reviewYear: String(year),
        periodStart: `${year}-01-01`,
        periodEnd: `${year}-12-31`,
        dueDate: null,
        status: 'open',
      },
      rules: {
        name: [required, shouldNotExceedCharLength(150)],
        year: [
          required,
          (v) =>
            /^(20\d{2})$/.test(String(v).trim()) || 'Enter a year such as 2025',
        ],
        periodStart: [required, validDateFormat(this.userDateFormat)],
        periodEnd: [
          required,
          validDateFormat(this.userDateFormat),
          (v) =>
            endDateShouldBeAfterStartDate(
              () => this.form.periodStart,
              'Period end must be after period start',
              {allowSameDate: true},
            )(v),
        ],
        dueDate: [validDateFormat(this.userDateFormat)],
      },
    };
  },
  computed: {
    subunitOptions() {
      return this.distinct('subunit');
    },
    jobTitleOptions() {
      return this.distinct('jobTitle');
    },
    filtered() {
      const text = (this.filters.text || '').trim().toLowerCase();
      const subunit = this.filters.subunit && this.filters.subunit.id;
      const jobTitle = this.filters.jobTitle && this.filters.jobTitle.id;
      return this.participants.filter(
        (p) =>
          (!text ||
            p.name.toLowerCase().includes(text) ||
            (p.employeeId || '').toLowerCase().includes(text)) &&
          (!subunit || p.subunit === subunit) &&
          (!jobTitle || p.jobTitle === jobTitle),
      );
    },
    selectedCount() {
      return this.participants.filter((p) => p.selected).length;
    },
  },
  created() {
    if (this.cycleId) {
      this.isLoading = true;
      Promise.all([
        this.http.get(this.cycleId),
        new APIService(
          window.appGlobal.baseUrl,
          `${API_BASE}/cycles/${this.cycleId}/participants`,
        ).getAll(),
      ])
        .then(([cycle, participants]) => {
          const c = cycle.data.data;
          this.form = {
            name: c.name,
            reviewYear: String(c.reviewYear),
            periodStart: c.periodStart,
            periodEnd: c.periodEnd,
            dueDate: c.dueDate,
            status: c.status,
          };
          this.participants = participants.data.data.participants;
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },
  methods: {
    distinct(field) {
      const values = new Set(
        this.participants.map((p) => p[field]).filter(Boolean),
      );
      return [...values].sort().map((v) => ({id: v, label: v}));
    },
    suggestName(year) {
      if (this.cycleId || !/^20\d{2}$/.test(String(year))) return;
      const y = Number(year);
      this.form.name = `${y} Review & ${y + 1} Plan`;
      this.form.periodStart = `${y}-01-01`;
      this.form.periodEnd = `${y}-12-31`;
    },
    setShown(value) {
      this.filtered.forEach((p) => {
        if (!p.locked) p.selected = value;
      });
    },
    payload() {
      return {
        name: this.form.name.trim(),
        reviewYear: Number(this.form.reviewYear),
        periodStart: this.form.periodStart,
        periodEnd: this.form.periodEnd,
        dueDate: this.form.dueDate || null,
        status: this.form.status,
      };
    },
    onSave() {
      this.isLoading = true;
      const request = this.cycleId
        ? this.http.update(this.cycleId, this.payload())
        : this.http.create(this.payload());
      request
        .then((response) => {
          this.$toast.saveSuccess();
          if (!this.cycleId) {
            navigate('/personalReview/saveReviewCycle/{id}', {
              id: response.data.data.id,
            });
          } else {
            this.isLoading = false;
          }
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    saveParticipants() {
      this.isLoading = true;
      const api = new APIService(
        window.appGlobal.baseUrl,
        `${API_BASE}/cycles/${this.cycleId}/participants`,
      );
      api
        .request({
          method: 'PUT',
          data: {
            empNumbers: this.participants
              .filter((p) => p.selected)
              .map((p) => p.empNumber),
          },
        })
        .then((response) => {
          const r = response.data.data;
          this.$toast.success({
            title: 'Participants saved',
            message: `${r.added} added, ${r.removed} removed${
              r.kept ? `, ${r.kept} kept (already started)` : ''
            }.`,
          });
          return api.getAll().then((again) => {
            this.participants = again.data.data.participants;
          });
        })
        .catch((e) =>
          this.$toast.error({title: 'Error', message: errorMessage(e)}),
        )
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCancel() {
      navigate('/personalReview/viewReviewCycles');
    },
  },
};
</script>

<style>
.apcwo-pr-picker {
  margin-top: 16px;
}
.apcwo-pr-picker__filters {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}
.apcwo-pr-picker__bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
}
.apcwo-pr-picker__list {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e3e3ea;
  border-radius: 8px;
}
.apcwo-pr-picker__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f4;
  font-size: 14px;
  cursor: pointer;
}
.apcwo-pr-picker__row.is-locked {
  background: #f8f8fb;
  cursor: default;
}
.apcwo-pr-picker__name {
  font-weight: 600;
}
.apcwo-pr-picker__meta {
  flex: 1;
  font-size: 12px;
  color: #8a8a99;
}
@media (max-width: 800px) {
  .apcwo-pr-picker__filters {
    grid-template-columns: 1fr;
  }
}
.apcwo-pr-root {
  font-family: 'Nunito Sans', sans-serif;
}
.apcwo-pr-root button,
.apcwo-pr-root input,
.apcwo-pr-root textarea,
.apcwo-pr-root select {
  font-family: inherit;
}
</style>
